import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { AppHeader } from './AppHeader';
import { PinLockOverlay } from './PinLockOverlay';
import { HelpFAB } from './HelpFAB';
import { PandaAIChat } from './PandaAIChat';
import { SessionExpiredBanner } from './SessionExpiredBanner';
import { SessionWarningBanner } from './SessionWarningBanner';
import { InstallPWABanner } from './InstallPWABanner';
import { OnboardingTour, useTourKeyboard } from './OnboardingTour';
import { OnboardingTourProvider, hasTourBeenSeen, useOnboardingTour } from '../../contexts/OnboardingTourContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePinLockContext } from '../../contexts/PinLockContext';
import { usePlanPermissions } from '../../hooks/usePlanPermissions';
import { useCardDueAlerts } from '../../hooks/useCardDueAlerts';
import { useCalendarPendingNotifications } from '../../hooks/useCalendarPendingNotifications';
import { NotificationToast } from './NotificationToast';
import { NotificationsProvider, useNotificationsContext } from '../../contexts/NotificationsContext';
import { useSessionManager } from '../../hooks/useSessionManager';
import { ConversionTriggersProvider, useConversionContext } from '../../contexts/ConversionTriggersContext';
import { ConversionNudge } from './ConversionNudge';

// ── Auto-start tour for first-time users ─────────────────────────────────────
const TourAutoStart = () => {
  const { user } = useAuth();
  const { startTour } = useOnboardingTour();

  useEffect(() => {
    if (!user?.id) return;
    // Small delay so app fully renders before starting tour
    const timer = setTimeout(() => {
      if (!hasTourBeenSeen(user.id)) {
        startTour(false);
      }
    }, 1200);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Register keyboard shortcuts
  useTourKeyboard();

  return null;
};

// Inner layout consumes the shared notifications context
const AppLayoutInner = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { user, profile, signOut, sessionExpired, clearSessionExpired } = useAuth();
  const permissions = usePlanPermissions();
  const pinLock = usePinLockContext();
  const conversion = useConversionContext();

  useSessionManager();
  useCardDueAlerts();
  useCalendarPendingNotifications();

  const { toastNotification, dismissToast, markRead } = useNotificationsContext();

  const displayName = profile?.display_name || profile?.name || user?.email?.split('@')[0] || 'Usuário';
  const fullName = profile?.full_name || displayName;
  const plan = profile?.plan || 'free';
  const googleAvatarUrl = profile?.auth_provider === 'google'
    ? (user?.user_metadata?.avatar_url ?? user?.user_metadata?.picture ?? null)
    : null;
  const avatarUrl = profile?.avatar_url ?? googleAvatarUrl;
  const initials = fullName.split(' ').slice(0, 2).map((n: string) => n[0]).join('').toUpperCase() || 'U';

  // ── 1. Wait for PIN check to complete (prevents flash of protected content) ──
  if (!pinLock.pinCheckComplete) {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center bg-[#F8FAFB]"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        <i className="ri-loader-4-line text-2xl text-emerald-500 animate-spin" />
      </div>
    );
  }

  // ── 2. Locked: render ONLY the lock screen (no content below) ─────────────
  if (pinLock.isLocked && pinLock.isPinEnabled) {
    return (
      <PinLockOverlay
        pinLock={pinLock}
        userName={displayName}
        avatarUrl={avatarUrl}
        initials={initials}
        onSignOut={signOut}
      />
    );
  }

  // ── 3. Unlocked: render full app layout ────────────────────────────────────
  return (
    <div
      className="flex h-screen overflow-hidden bg-[#F8FAFB]"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      <Sidebar
        isCollapsed={isCollapsed}
        onToggle={() => setIsCollapsed((v) => !v)}
        mobileOpen={isMobileOpen}
        onMobileClose={() => setIsMobileOpen(false)}
        plan={plan}
        userName={displayName}
        avatarUrl={avatarUrl}
        avatarInitials={initials}
        permissions={permissions}
      />

      <div
        className={`flex flex-col flex-1 min-w-0 h-full transition-all duration-300 ${
          isCollapsed ? 'lg:ml-[68px]' : 'lg:ml-[264px]'
        }`}
      >
        {/* Spacer that reserves room for the fixed AppHeader */}
        <div className="h-[72px] w-full shrink-0" aria-hidden="true" />

        <AppHeader
          isCollapsed={isCollapsed}
          onToggleSidebar={() => setIsCollapsed((v) => !v)}
          onOpenMobileSidebar={() => setIsMobileOpen(true)}
          onSignOut={signOut}
          user={{ name: displayName, fullName, avatarInitials: initials, plan, planName: permissions.planName, avatarUrl }}
        />

        {/* Content area: min-h garante que empty states nunca "encolham" abaixo do viewport útil.
            72px = altura do AppHeader (h-[72px]). O inner-div carrega o padding para que
            min-h-full funcione corretamente no contexto do scroll container. */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          <div className="min-h-[calc(100vh-72px)] w-full p-4 md:p-6 lg:p-8 flex flex-col">
            <Outlet />
          </div>
        </main>
      </div>

      {sessionExpired && <SessionExpiredBanner onDismiss={clearSessionExpired} />}
      <SessionWarningBanner />

      {/* Panda AI floating chat — visible only for non-admin users with access */}
      <PandaAIChat />

      <HelpFAB />

      <NotificationToast
        notification={toastNotification}
        onDismiss={dismissToast}
        onMarkRead={markRead}
      />

      {/* PWA install banner — shown once on mobile for authenticated users */}
      <InstallPWABanner />

      {/* Product Tour overlay */}
      <OnboardingTour />

      {/* Auto-start + keyboard shortcuts */}
      <TourAutoStart />

      {/* Conversion nudge — non-intrusive, shows smart upgrade moments */}
      <ConversionNudge
        nudge={conversion.activeNudge}
        onDismiss={conversion.dismissNudge}
        onUpgradeClick={conversion.handleUpgradeClick}
      />
    </div>
  );
};

export const AppLayout = () => {
  const { user } = useAuth();
  return (
    <NotificationsProvider>
      <OnboardingTourProvider userId={user?.id ?? ''}>
        <ConversionTriggersProvider>
          <AppLayoutInner />
        </ConversionTriggersProvider>
      </OnboardingTourProvider>
    </NotificationsProvider>
  );
};
